/**
 * DeliverableForm - Deliverable row with inline editing, auto-save, and creation support.
 *
 * A unified component for both displaying/editing saved deliverables and creating new ones.
 * In 'edit' mode: Uses form state, auto-saves changes, shows drag handle and delete button.
 * In 'create' mode: Uses local state, submits on Enter/click, shows "+" icon for creation.
 */

import { useState, useRef, useMemo, useCallback, memo, type ReactNode } from 'react';
import { useFormContext, useWatch, Controller } from 'react-hook-form';
import { Trash2, Loader2, Plus } from 'lucide-react';
import { IconButton } from '@lean-agility/design-system/react';
import { DatePicker } from '@lean-agility/design-system/react';
import { SortableHandle, SaveStatusIndicator, SourceIdBadge, InlineInput, DependencyChip } from '@/components/atoms';
import {
  SearchableSelect,
  DependencyPicker,
  Collapsible,
  type SearchableSelectOption,
  type GroupedPredecessors,
  type PredecessorInfo,
} from '@/components/molecules';
import type { SimulationFormValues, FeatureFormValues } from '@/types/api';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { cn } from '@lean-agility/design-system/react';
import { type DeliverableOption } from '@/lib/utils';
import { useCreateDependency, useDeleteDependency, useCreateDeliverable } from '@/hooks/queries';
import { useAutoSave, useErrorHandler, useSessionState } from '@/hooks';
import { useWriteErrors } from '@/hooks/useWriteErrors';
import { updateDeliverable } from '@/lib/api';
import { availableToStartHasMin, availableToStartIsOrdered, unknownRangeIsOrdered } from '@/lib/validation';

// ============================================================================
// Type Definitions
// ============================================================================

interface DeliverableFormBaseProps {
  /** Layout variant */
  variant?: 'table' | 'card';
  /** Available features for the feature dropdown */
  availableFeatures: FeatureFormValues[];
  /** Simulation ID for API calls */
  simulationId?: string | null;
}

/**
 * One deliverable that lists the current one as a predecessor — the "Blocking"
 * direction, derived by `TeamForm` rather than stored on the row.
 *
 * Exported as the single source of truth for that shape: the producer
 * (`TeamForm.blockingByDeliverableId`) and this consumer must agree, and when
 * they did not, `sourceId` was rendered in the "Blocked by" direction and
 * silently missing here.
 */
export interface BlockingDeliverable {
  id: string;
  teamName: string;
  teamColor: string;
  deliverableName: string;
  /**
   * External tracker key; distinguishes same-named slices of one epic.
   *
   * Required, not optional: the producer can always derive it, and making it
   * optional is exactly what let it go missing in this direction while the
   * "Blocked by" direction rendered it. The compiler now asks the question a
   * test would have to.
   */
  sourceId: string | null;
  priority: number;
  teamIndex: number;
  deliverableIndex: number;
}

interface DeliverableFormEditProps extends DeliverableFormBaseProps {
  mode: 'edit';
  id: string;
  teamIndex: number;
  teamId: string;
  deliverableIndex: number;
  onRemove: () => void;
  canRemove: boolean;
  availablePredecessors: DeliverableOption[];
  onDependencyClick?: (deliverableId: string, teamName: string) => void;
  highlightedDeliverableId?: string | null;
  blockingDeliverables?: BlockingDeliverable[];
  /** Callback to remove a blocking relationship (inverse of predecessor removal) */
  onRemoveBlocking?: (
    currentDeliverableId: string,
    blockerTeamIndex: number,
    blockerDeliverableIndex: number,
    blockerDeliverableId: string,
  ) => void;
}

interface DeliverableFormCreateProps extends DeliverableFormBaseProps {
  mode: 'create';
  teamId: string;
  // These are not used in create mode
  id?: never;
  teamIndex?: never;
  deliverableIndex?: never;
  onRemove?: never;
  canRemove?: never;
  availablePredecessors?: never;
  onDependencyClick?: never;
  highlightedDeliverableId?: never;
  blockingDeliverables?: never;
  onRemoveBlocking?: never;
}

export type DeliverableFormProps = DeliverableFormEditProps | DeliverableFormCreateProps;

// ============================================================================
// Main Component
// ============================================================================

export const DeliverableForm = memo(function DeliverableForm(props: DeliverableFormProps) {
  if (props.mode === 'create') {
    return <DeliverableFormCreate {...props} />;
  }
  return <DeliverableFormEdit {...props} />;
});

// ============================================================================
// Edit Mode Component
// ============================================================================

// `GroupedPredecessors` and `PredecessorInfo` are imported from the picker (the
// single source of truth) rather than redefined here.

function DeliverableFormEdit({
  id,
  teamIndex,
  teamId,
  deliverableIndex,
  onRemove,
  canRemove,
  availablePredecessors,
  availableFeatures,
  simulationId,
  variant = 'table',
  onDependencyClick,
  highlightedDeliverableId,
  blockingDeliverables = [],
  onRemoveBlocking,
}: DeliverableFormEditProps) {
  // Get deliverable name for error context (will be watched below)
  const { control: formControl } = useFormContext<SimulationFormValues>();
  const deliverableName =
    useWatch({ control: formControl, name: `teams.${teamIndex}.deliverables.${deliverableIndex}.name` }) ||
    `Deliverable ${deliverableIndex + 1}`;
  const { handleError } = useErrorHandler(`Deliverable: ${deliverableName}`);
  const { onSaveError } = useWriteErrors('deliverable', handleError);
  const {
    register,
    formState: { errors, touchedFields },
    control,
    setValue,
    getValues,
  } = useFormContext<SimulationFormValues>();

  // Get deliverable ID from form - useWatch is more performant than watch()
  const deliverableId = useWatch({ control, name: `teams.${teamIndex}.deliverables.${deliverableIndex}.id` });

  // Watch all values that need auto-save
  const watchedName = useWatch({ control, name: `teams.${teamIndex}.deliverables.${deliverableIndex}.name` });
  const watchedWorkItems = useWatch({
    control,
    name: `teams.${teamIndex}.deliverables.${deliverableIndex}.unfinished_work_items`,
  });
  const watchedUnknownMin = useWatch({
    control,
    name: `teams.${teamIndex}.deliverables.${deliverableIndex}.unknown_min`,
  });
  const watchedUnknownMax = useWatch({
    control,
    name: `teams.${teamIndex}.deliverables.${deliverableIndex}.unknown_max`,
  });
  const watchedDueDate = useWatch({ control, name: `teams.${teamIndex}.deliverables.${deliverableIndex}.due_date` });
  const watchedAtsMin = useWatch({
    control,
    name: `teams.${teamIndex}.deliverables.${deliverableIndex}.available_to_start_min`,
  });
  const watchedAtsMax = useWatch({
    control,
    name: `teams.${teamIndex}.deliverables.${deliverableIndex}.available_to_start_max`,
  });
  const watchedFeatureId = useWatch({
    control,
    name: `teams.${teamIndex}.deliverables.${deliverableIndex}.feature_id`,
  });
  const watchedSourceId = useWatch({ control, name: `teams.${teamIndex}.deliverables.${deliverableIndex}.source_id` });
  const watchedSourceUrl = useWatch({
    control,
    name: `teams.${teamIndex}.deliverables.${deliverableIndex}.source_url`,
  });

  // Check if this deliverable should be highlighted (for navigation)
  const isHighlighted = highlightedDeliverableId && deliverableId === highlightedDeliverableId;

  // Unified auto-save hook - replaces ~80 lines of manual logic.
  // The number inputs register raw strings, so coerce them to the saved shape
  // (number / number | null) here. This both matches the API payload type and
  // keeps the same values the per-field transforms produced previously.
  const unknownMin = watchedUnknownMin === null || watchedUnknownMin === '' ? null : Number(watchedUnknownMin);
  const unknownMax = watchedUnknownMax === null || watchedUnknownMax === '' ? null : Number(watchedUnknownMax);
  // Hold every save while a min/max pair breaks its schema refine (the field is
  // already red): the API judges the stored row merged with the patch, so a
  // half-edited pair would only be refused. Once the pair is valid again, the
  // held edits go out together in one PATCH (shipcast-97d1).
  const pairsAreValid =
    unknownRangeIsOrdered(unknownMin, unknownMax) &&
    availableToStartHasMin(watchedAtsMin, watchedAtsMax) &&
    availableToStartIsOrdered(watchedAtsMin, watchedAtsMax);
  const { status: saveStatus } = useAutoSave({
    values: {
      name: watchedName,
      unfinished_work_items: Number(watchedWorkItems),
      unknown_min: unknownMin,
      unknown_max: unknownMax,
      due_date: watchedDueDate,
      available_to_start_min: watchedAtsMin,
      available_to_start_max: watchedAtsMax,
      feature_id: watchedFeatureId,
    },
    fields: [
      { name: 'name' },
      { name: 'unfinished_work_items' },
      { name: 'unknown_min' },
      { name: 'unknown_max' },
      { name: 'due_date', transform: (v) => v || null },
      { name: 'available_to_start_min', transform: (v) => v || null },
      { name: 'available_to_start_max', transform: (v) => v || null },
      { name: 'feature_id', transform: (v) => v || null },
    ],
    saveFn: (payload) => (deliverableId ? updateDeliverable(deliverableId, payload) : Promise.resolve()),
    enabled: !!simulationId && !!teamId && !!deliverableId && pairsAreValid,
    onError: onSaveError,
  });

  // Details section expand/collapse (persisted per deliverable across navigation)
  const [isDetailsExpanded, setIsDetailsExpanded] = useSessionState(`deliverable-details-${deliverableId}`, false);

  // TanStack Query mutations for dependencies only
  const createDependencyMutation = useCreateDependency(simulationId);
  const deleteDependencyMutation = useDeleteDependency(simulationId);

  // All deliverables in this component are saved (have an ID), so drag is always enabled
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 1000 : 'auto',
  };

  const deliverableErrors = errors.teams?.[teamIndex]?.deliverables?.[deliverableIndex];
  const deliverableTouched = touchedFields.teams?.[teamIndex]?.deliverables?.[deliverableIndex];
  const currentDeliverable = useWatch({ control, name: `teams.${teamIndex}.deliverables.${deliverableIndex}` });
  const currentTeamName = useWatch({ control, name: `teams.${teamIndex}.name` });
  const watchedPredecessors = useWatch({
    control,
    name: `teams.${teamIndex}.deliverables.${deliverableIndex}.predecessors`,
  });
  const predecessors = useMemo(() => watchedPredecessors || [], [watchedPredecessors]);

  // Sort predecessors by team position, then deliverable position within team
  // availablePredecessors is already ordered correctly (teams by position, deliverables by position)
  const sortedPredecessors = useMemo(() => {
    const orderMap = new Map(availablePredecessors.map((opt, index) => [opt.id, index]));
    return [...predecessors].sort((a, b) => {
      const orderA = orderMap.get(a) ?? Infinity;
      const orderB = orderMap.get(b) ?? Infinity;
      return orderA - orderB;
    });
  }, [predecessors, availablePredecessors]);

  // Get the current deliverable's UUID
  const currentDeliverableId = currentDeliverable?.id || '';

  // Build a lookup map from UUID to display info for showing selected predecessors
  const predecessorDisplayLookup = useMemo(() => {
    const lookup: Record<string, PredecessorInfo> = {};
    for (const opt of availablePredecessors) {
      lookup[opt.id] = {
        teamName: opt.teamName,
        teamColor: opt.teamColor,
        deliverableName: opt.deliverableName,
        featureName: opt.featureName,
        priority: opt.priority,
        // Carry the external key so selected pills show the source-id badge,
        // keeping same-named slices distinguishable after selection (not just
        // while picking).
        sourceId: opt.sourceId,
      };
    }
    return lookup;
  }, [availablePredecessors]);

  // Collapsed summary for the Details section — pill-style segments with named dependencies
  const detailsSummary = useMemo(() => {
    const pills: ReactNode[] = [];
    // Work items: show total range (base + uncertainty)
    const base = Number(watchedWorkItems) || 0;
    const minExtra = Number(watchedUnknownMin) || 0;
    const maxExtra = Number(watchedUnknownMax) || minExtra;
    const totalMin = base + minExtra;
    const totalMax = base + maxExtra;
    if (totalMin > 0 || totalMax > 0) {
      pills.push(
        <span key="wi" className="rounded-sm bg-bg-muted/50 px-1.5 py-0.5 text-xs text-text-secondary">
          {totalMin === totalMax ? `${totalMin} items` : `${totalMin}–${totalMax} items`}
        </span>,
      );
    }
    if (watchedDueDate) {
      pills.push(
        <span key="due" className="rounded-sm bg-bg-muted/50 px-1.5 py-0.5 text-xs text-text-secondary">
          Due {watchedDueDate}
        </span>,
      );
    }
    if (watchedAtsMin) {
      pills.push(
        <span key="ats" className="rounded-sm bg-bg-muted/50 px-1.5 py-0.5 text-xs text-text-secondary">
          Available {watchedAtsMin}
          {watchedAtsMax ? ` – ${watchedAtsMax}` : ''}
        </span>,
      );
    }
    // Dependencies: show actual deliverable names as team-colored inner chips
    if (predecessors.length > 0) {
      pills.push(
        <span key="dep" className="inline-flex items-center gap-1 rounded-sm bg-bg-muted/30 px-1.5 py-0.5 text-xs">
          <span className="text-text-muted">Blocked by</span>
          {predecessors.map((id) => {
            const pred = predecessorDisplayLookup[id];
            const color = pred?.teamColor || '#64748b';
            return (
              <span
                key={id}
                className="rounded-sm px-1 py-px font-medium"
                style={{ backgroundColor: `${color}25`, color }}
              >
                {pred?.priority != null ? `#${pred.priority} ` : ''}
                {pred?.deliverableName || '?'}
                {/* External key as a light monospace suffix (not the full badge
                    used in the picker) — keeps this dense collapsed summary
                    scannable while still distinguishing same-named epic slices. */}
                {pred?.sourceId ? <span className="ml-1 font-mono font-normal opacity-70">{pred.sourceId}</span> : null}
              </span>
            );
          })}
        </span>,
      );
    }
    if (blockingDeliverables.length > 0) {
      pills.push(
        <span key="blk" className="inline-flex items-center gap-1 rounded-sm bg-bg-muted/30 px-1.5 py-0.5 text-xs">
          <span className="text-text-muted">Blocking</span>
          {blockingDeliverables.map((b) => {
            const color = b.teamColor || '#64748b';
            return (
              <span
                key={b.id}
                className="rounded-sm px-1 py-px font-medium"
                style={{ backgroundColor: `${color}25`, color }}
              >
                #{b.priority} {b.deliverableName}
                {/* Same monospace suffix the "Blocked by" pills above use — the
                    two directions must read identically. */}
                {b.sourceId ? <span className="ml-1 font-mono font-normal opacity-70">{b.sourceId}</span> : null}
              </span>
            );
          })}
        </span>,
      );
    }
    if (pills.length === 0) return undefined;
    return <span className="flex flex-wrap items-center gap-1">{pills}</span>;
  }, [
    watchedWorkItems,
    watchedUnknownMin,
    watchedUnknownMax,
    watchedDueDate,
    watchedAtsMin,
    watchedAtsMax,
    predecessors,
    predecessorDisplayLookup,
    blockingDeliverables,
  ]);

  // Filter out current deliverable and already added predecessors
  const filteredPredecessors = useMemo(() => {
    return availablePredecessors.filter((opt) => opt.id !== currentDeliverableId && !predecessors.includes(opt.id));
  }, [availablePredecessors, currentDeliverableId, predecessors]);

  // Group predecessors by team
  const groupedPredecessors = useMemo((): GroupedPredecessors[] => {
    const groups: Map<string, { id: string; deliverableName: string; sourceId?: string | null }[]> = new Map();

    for (const opt of filteredPredecessors) {
      if (!groups.has(opt.teamName)) {
        groups.set(opt.teamName, []);
      }
      groups.get(opt.teamName)!.push({
        id: opt.id,
        deliverableName: opt.deliverableName,
        sourceId: opt.sourceId,
      });
    }

    // Convert to array and sort: current team first, then alphabetically
    const result: GroupedPredecessors[] = [];

    // Add current team first if it has deliverables
    if (groups.has(currentTeamName)) {
      result.push({
        teamName: currentTeamName,
        deliverables: groups.get(currentTeamName)!.sort((a, b) => a.deliverableName.localeCompare(b.deliverableName)),
        isCurrentTeam: true,
      });
      groups.delete(currentTeamName);
    }

    // Add other teams alphabetically
    const otherTeams = Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([teamName, deliverables]) => ({
        teamName,
        deliverables: deliverables.sort((a, b) => a.deliverableName.localeCompare(b.deliverableName)),
        isCurrentTeam: false,
      }));

    result.push(...otherTeams);
    return result;
  }, [filteredPredecessors, currentTeamName]);

  // Show errors when field is touched (all items are already saved)
  const shouldShowError = (fieldName: keyof NonNullable<typeof deliverableTouched>): boolean => {
    return !!deliverableTouched?.[fieldName];
  };

  // Get current feature value (already watched above as watchedFeatureId)
  const currentFeatureId = watchedFeatureId;

  // Only saved features (with UUIDs) can be assigned to deliverables
  const savedFeatures = availableFeatures.filter((f) => f.id);
  const hasUnsavedFeatures = availableFeatures.some((f) => !f.id);

  // Convert saved features to SearchableSelect options (with priority number)
  const featureOptions: SearchableSelectOption[] = useMemo(
    () =>
      savedFeatures.map((f, index) => ({
        value: f.id!,
        label: `${index + 1}. ${f.name}`,
      })),
    [savedFeatures],
  );

  // Handle feature selection change
  const handleFeatureChange = useCallback(
    (featureId: string | null) => {
      setValue(`teams.${teamIndex}.deliverables.${deliverableIndex}.feature_id`, featureId);
    },
    [setValue, teamIndex, deliverableIndex],
  );

  const addPredecessor = useCallback(
    (predecessorId: string) => {
      const current = getValues(`teams.${teamIndex}.deliverables.${deliverableIndex}.predecessors`) || [];

      // Update local form state immediately for responsive UI (stores UUID)
      setValue(`teams.${teamIndex}.deliverables.${deliverableIndex}.predecessors`, [...current, predecessorId]);

      // Call API if we have IDs (persistent simulation)
      if (simulationId && deliverableId && teamId) {
        createDependencyMutation.mutate(
          { deliverableId, predecessorId, teamId },
          {
            onError: (err) => {
              // Revert on error
              setValue(`teams.${teamIndex}.deliverables.${deliverableIndex}.predecessors`, current);
              handleError(err);
            },
          },
        );
      }
    },
    [
      getValues,
      setValue,
      teamIndex,
      deliverableIndex,
      simulationId,
      deliverableId,
      teamId,
      createDependencyMutation,
      handleError,
    ],
  );

  const removePredecessor = useCallback(
    (predecessorIdToRemove: string) => {
      const current = getValues(`teams.${teamIndex}.deliverables.${deliverableIndex}.predecessors`) || [];

      // Update local form state immediately for responsive UI
      setValue(
        `teams.${teamIndex}.deliverables.${deliverableIndex}.predecessors`,
        current.filter((id) => id !== predecessorIdToRemove),
      );

      // Call API if we have IDs (persistent simulation)
      if (simulationId && deliverableId && predecessorIdToRemove && teamId) {
        deleteDependencyMutation.mutate(
          { deliverableId, predecessorId: predecessorIdToRemove, teamId },
          {
            onError: (err) => {
              // Revert on error
              setValue(`teams.${teamIndex}.deliverables.${deliverableIndex}.predecessors`, current);
              handleError(err);
            },
          },
        );
      }
    },
    [
      getValues,
      setValue,
      teamIndex,
      deliverableIndex,
      simulationId,
      deliverableId,
      teamId,
      deleteDependencyMutation,
      handleError,
    ],
  );

  // Handle dependency chip click for navigation
  const handleDependencyChipClick = useCallback(
    (predecessorId: string, teamName: string) => {
      onDependencyClick?.(predecessorId, teamName);
    },
    [onDependencyClick],
  );

  if (variant === 'table') {
    return (
      <div
        ref={setNodeRef}
        style={style}
        data-deliverable-id={deliverableId}
        className={cn(
          'group overflow-hidden rounded-xl border transition-all duration-200',
          isHighlighted
            ? `border-action-primary ring-2 ring-action-primary ring-offset-2 ring-offset-bg-elevated`
            : isDragging
              ? 'border-border-accent shadow-lg'
              : `
                border-border-default/50
                hover:border-border-default/70 hover:shadow-xs
              `,
        )}
      >
        {/* Row 1: Gutter + Name/Feature + Delete — pinned top row */}
        <div className="flex gap-0">
          {/* Gutter - Handle + Number badge */}
          <div
            className={cn(
              `
                flex w-14 shrink-0 cursor-grab items-center justify-center gap-1 transition-colors
                active:cursor-grabbing
              `,
              isHighlighted
                ? 'bg-action-primary/5'
                : isDragging
                  ? 'bg-bg-accent-muted/40'
                  : `
                    bg-bg-elevated
                    hover:bg-bg-muted/20
                  `,
            )}
            {...attributes}
            {...listeners}
          >
            <SortableHandle />
            <div
              className="
                flex size-7 items-center justify-center rounded-md border border-border-default/60 bg-bg-subtle text-xs
                font-bold text-text-secondary shadow-xs
              "
            >
              <SaveStatusIndicator status={saveStatus} idleContent={deliverableIndex + 1} />
            </div>
          </div>

          {/* Name + Feature */}
          <div
            className={cn(
              `flex flex-1 items-center gap-2 px-3 py-2.5 transition-all duration-200`,
              isHighlighted ? 'bg-action-primary/5' : isDragging ? `bg-bg-elevated` : `bg-bg-elevated`,
            )}
          >
            {/* Source ID Badge (if available) */}
            {watchedSourceId && (
              <SourceIdBadge sourceId={watchedSourceId} href={watchedSourceUrl} size="md" className="shrink-0" />
            )}

            {/* Deliverable Name - PRIMARY (larger, prominent) */}
            <div className="min-w-37.5 flex-1">
              <InlineInput
                type="text"
                size="md"
                placeholder="Deliverable name"
                title={deliverableErrors?.name?.message || watchedName || undefined}
                error={!!deliverableErrors?.name && shouldShowError('name')}
                {...register(`teams.${teamIndex}.deliverables.${deliverableIndex}.name`)}
              />
            </div>

            {/* Feature Selection - secondary, right-aligned */}
            <div className="w-60 shrink-0">
              <SearchableSelect
                value={currentFeatureId || null}
                onChange={handleFeatureChange}
                options={featureOptions}
                placeholder="No feature"
                allowClear
                clearLabel="No feature (ungrouped)"
                searchPlaceholder="Search features..."
                noOptionsMessage={
                  hasUnsavedFeatures && savedFeatures.length === 0
                    ? 'Finish typing feature names to save them'
                    : hasUnsavedFeatures
                      ? 'Some features are still saving...'
                      : 'No features available'
                }
                error={deliverableErrors?.feature_id?.message}
              />
            </div>
          </div>

          {/* Delete button */}
          <div
            className={cn(
              'flex w-12 shrink-0 items-center justify-center transition-colors',
              isHighlighted ? 'bg-action-primary/5' : isDragging ? `bg-bg-elevated` : `bg-bg-elevated`,
            )}
          >
            {canRemove ? (
              <IconButton
                type="button"
                variant="ghost-danger"
                size="md"
                onClick={onRemove}
                aria-label="Delete deliverable"
              >
                <Trash2 className="size-5" />
              </IconButton>
            ) : (
              <div className="w-10" />
            )}
          </div>
        </div>

        {/* Row 2: Details (collapsible) - Work items + Dependencies + Timing */}
        <Collapsible
          header={<span className="text-xs font-medium text-text-secondary">Details</span>}
          summary={detailsSummary}
          isExpanded={isDetailsExpanded}
          onExpandedChange={setIsDetailsExpanded}
          defaultExpanded={false}
          className="border-t border-border-default/20 px-3"
          contentClassName="pt-1 pb-2"
        >
          <div className="flex flex-col gap-2">
            {/* Work items with uncertainty range */}
            <div className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-xs text-text-muted">Work items</span>
              <div className="flex items-center gap-1">
                <div className="w-12">
                  <InlineInput
                    type="number"
                    size="xs"
                    align="center"
                    min={0}
                    placeholder="-"
                    title={deliverableErrors?.unfinished_work_items?.message || undefined}
                    error={!!deliverableErrors?.unfinished_work_items && shouldShowError('unfinished_work_items')}
                    {...register(`teams.${teamIndex}.deliverables.${deliverableIndex}.unfinished_work_items`)}
                  />
                </div>
                <span className="text-xs text-text-muted">plus</span>
                <div className="w-10">
                  <InlineInput
                    type="number"
                    size="xs"
                    align="center"
                    min={0}
                    placeholder="0"
                    title={deliverableErrors?.unknown_min?.message || 'Leave empty to default to 0 in simulation'}
                    error={!!deliverableErrors?.unknown_min && shouldShowError('unknown_min')}
                    {...register(`teams.${teamIndex}.deliverables.${deliverableIndex}.unknown_min`)}
                  />
                </div>
                <span className="text-xs text-text-muted">to</span>
                <div className="w-10">
                  <InlineInput
                    type="number"
                    size="xs"
                    align="center"
                    min={0}
                    placeholder={String(watchedUnknownMin || 0)}
                    title={
                      deliverableErrors?.unknown_max?.message || 'Leave empty to default to min value in simulation'
                    }
                    error={!!deliverableErrors?.unknown_max && shouldShowError('unknown_max')}
                    {...register(`teams.${teamIndex}.deliverables.${deliverableIndex}.unknown_max`)}
                  />
                </div>
                <span className="text-xs text-text-muted">more</span>
              </div>
            </div>
            {/* Blocked by */}
            <div className="flex flex-wrap items-center gap-1">
              <span className="w-24 shrink-0 text-xs text-text-muted">Blocked by</span>
              <DependencyPicker
                selectedPredecessors={sortedPredecessors}
                predecessorLookup={predecessorDisplayLookup}
                availableGroups={groupedPredecessors}
                filteredCount={filteredPredecessors.length}
                onAdd={addPredecessor}
                onRemove={removePredecessor}
                onChipClick={onDependencyClick ? handleDependencyChipClick : undefined}
              />
            </div>
            {/* Blocking (reverse dependencies — X removes current deliverable from blocker's predecessors) */}
            <div className="flex flex-wrap items-center gap-1">
              <span className="w-24 shrink-0 text-xs text-text-muted">Blocking</span>
              {blockingDeliverables.length > 0 ? (
                blockingDeliverables.map((blocking) => (
                  <DependencyChip
                    key={blocking.id}
                    dependencyId={blocking.id}
                    name={`#${blocking.priority} ${blocking.deliverableName}`}
                    teamColor={blocking.teamColor}
                    maxWidth="none"
                    tooltip={`${blocking.teamName} | ${blocking.deliverableName}`}
                    sourceId={blocking.sourceId}
                    onClick={onDependencyClick ? () => onDependencyClick(blocking.id, blocking.teamName) : undefined}
                    onRemove={
                      onRemoveBlocking
                        ? () =>
                            onRemoveBlocking(
                              currentDeliverableId,
                              blocking.teamIndex,
                              blocking.deliverableIndex,
                              blocking.id,
                            )
                        : undefined
                    }
                  />
                ))
              ) : (
                <span className="text-xs text-text-muted italic">none</span>
              )}
            </div>
            {/* Due date */}
            <div className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-xs text-text-muted">Due date</span>
              <div className="w-32">
                <Controller
                  name={`teams.${teamIndex}.deliverables.${deliverableIndex}.due_date`}
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      value={field.value || ''}
                      className="w-full"
                      placeholder="Due date"
                      clearable
                      showToday={false}
                    />
                  )}
                />
              </div>
            </div>
            {/* Available from */}
            <div className="flex items-center gap-2">
              <span className="w-24 shrink-0 text-xs text-text-muted">Available from</span>
              <div className="w-32">
                <Controller
                  name={`teams.${teamIndex}.deliverables.${deliverableIndex}.available_to_start_min`}
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      value={field.value || ''}
                      className="w-full"
                      placeholder="From"
                      clearable
                      showToday={false}
                    />
                  )}
                />
              </div>
              <span className="text-xs text-text-muted">to</span>
              <div className="w-32">
                <Controller
                  name={`teams.${teamIndex}.deliverables.${deliverableIndex}.available_to_start_max`}
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      {...field}
                      value={field.value || ''}
                      className="w-full"
                      placeholder="To"
                      clearable
                      showToday={false}
                    />
                  )}
                />
              </div>
            </div>
          </div>
        </Collapsible>
      </div>
    );
  }

  // Card variant for small screens
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-deliverable-id={deliverableId}
      className={cn(
        `group w-full overflow-hidden rounded-xl border transition-all duration-200`,
        isHighlighted
          ? `border-action-primary bg-action-primary/5 ring-2 ring-action-primary ring-offset-2 ring-offset-bg-elevated`
          : isDragging
            ? 'scale-[1.02] border-border-accent bg-bg-subtle shadow-lg'
            : `
              border-border-default/30 bg-bg-elevated
              hover:border-border-default/50 hover:shadow-xs
            `,
      )}
    >
      {/* Row 1: Primary - Handle + # + Source ID + Deliverable name + Delete */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        {/* Drag Handle */}
        <SortableHandle listeners={listeners} attributes={attributes} className="shrink-0" />

        {/* Order Number with Status */}
        <div
          className="
            flex size-7 shrink-0 items-center justify-center rounded-md border border-border-default/60 bg-bg-subtle
            text-xs font-bold text-text-secondary shadow-xs
          "
        >
          <SaveStatusIndicator status={saveStatus} idleContent={deliverableIndex + 1} size="md" />
        </div>

        {/* Source ID Badge (if available) */}
        {watchedSourceId && (
          <SourceIdBadge sourceId={watchedSourceId} href={watchedSourceUrl} size="md" className="shrink-0" />
        )}

        {/* Deliverable Name - PRIMARY (larger, prominent) */}
        <InlineInput
          type="text"
          size="md"
          placeholder="Deliverable name"
          title={deliverableErrors?.name?.message || watchedName || undefined}
          error={!!deliverableErrors?.name && shouldShowError('name')}
          className="min-w-0 flex-1"
          {...register(`teams.${teamIndex}.deliverables.${deliverableIndex}.name`)}
        />

        {/* Delete Button */}
        {canRemove && (
          <IconButton
            type="button"
            variant="ghost-danger"
            size="sm"
            onClick={onRemove}
            className="shrink-0"
            aria-label="Delete deliverable"
          >
            <Trash2 className="size-4" />
          </IconButton>
        )}
      </div>

      {/* Row 2: Feature Selection */}
      <div className="flex items-center gap-2 border-t border-border-default/20 px-3 py-1.5">
        <div className="min-w-0 flex-1">
          <SearchableSelect
            value={currentFeatureId || null}
            onChange={handleFeatureChange}
            options={featureOptions}
            placeholder="No feature"
            allowClear
            clearLabel="No feature (ungrouped)"
            searchPlaceholder="Search features..."
            noOptionsMessage={
              hasUnsavedFeatures && savedFeatures.length === 0
                ? 'Finish typing feature names to save them'
                : hasUnsavedFeatures
                  ? 'Some features are still saving...'
                  : 'No features available'
            }
            error={deliverableErrors?.feature_id?.message}
          />
        </div>
      </div>

      {/* Row 3: Details (collapsible) - Work items + Dependencies + Timing */}
      <Collapsible
        header={<span className="text-xs font-medium text-text-secondary">Details</span>}
        summary={detailsSummary}
        isExpanded={isDetailsExpanded}
        onExpandedChange={setIsDetailsExpanded}
        defaultExpanded={false}
        className="px-3"
        contentClassName="pt-1 pb-2"
      >
        <div className="flex flex-col gap-2">
          {/* Work items with uncertainty range */}
          <div className="flex items-center gap-2">
            <span className="w-24 shrink-0 text-xs text-text-muted">Work items</span>
            <div className="flex items-center gap-1">
              <div className="w-12">
                <InlineInput
                  type="number"
                  size="xs"
                  align="center"
                  min={0}
                  placeholder="-"
                  title={deliverableErrors?.unfinished_work_items?.message || undefined}
                  error={!!deliverableErrors?.unfinished_work_items && shouldShowError('unfinished_work_items')}
                  {...register(`teams.${teamIndex}.deliverables.${deliverableIndex}.unfinished_work_items`)}
                />
              </div>
              <span className="text-xs text-text-muted">plus</span>
              <div className="w-10">
                <InlineInput
                  type="number"
                  size="xs"
                  align="center"
                  min={0}
                  placeholder="0"
                  title={deliverableErrors?.unknown_min?.message || undefined}
                  error={!!deliverableErrors?.unknown_min && shouldShowError('unknown_min')}
                  {...register(`teams.${teamIndex}.deliverables.${deliverableIndex}.unknown_min`)}
                />
              </div>
              <span className="text-xs text-text-muted">to</span>
              <div className="w-10">
                <InlineInput
                  type="number"
                  size="xs"
                  align="center"
                  min={0}
                  placeholder="0"
                  title={deliverableErrors?.unknown_max?.message || undefined}
                  error={!!deliverableErrors?.unknown_max && shouldShowError('unknown_max')}
                  {...register(`teams.${teamIndex}.deliverables.${deliverableIndex}.unknown_max`)}
                />
              </div>
              <span className="text-xs text-text-muted">more</span>
            </div>
          </div>
          {/* Blocked by */}
          <div className="flex flex-wrap items-center gap-1">
            <span className="w-24 shrink-0 text-xs text-text-muted">Blocked by</span>
            <DependencyPicker
              selectedPredecessors={sortedPredecessors}
              predecessorLookup={predecessorDisplayLookup}
              availableGroups={groupedPredecessors}
              filteredCount={filteredPredecessors.length}
              onAdd={addPredecessor}
              onRemove={removePredecessor}
              onChipClick={onDependencyClick ? handleDependencyChipClick : undefined}
              addLabel="+ Add"
            />
          </div>
          {/* Blocking (reverse dependencies — X removes current deliverable from blocker's predecessors) */}
          <div className="flex flex-wrap items-center gap-1">
            <span className="w-24 shrink-0 text-xs text-text-muted">Blocking</span>
            {blockingDeliverables.length > 0 ? (
              blockingDeliverables.map((blocking) => (
                <DependencyChip
                  key={blocking.id}
                  dependencyId={blocking.id}
                  name={`#${blocking.priority} ${blocking.deliverableName}`}
                  teamColor={blocking.teamColor}
                  maxWidth="none"
                  tooltip={`${blocking.teamName} | ${blocking.deliverableName}`}
                  sourceId={blocking.sourceId}
                  onClick={onDependencyClick ? () => onDependencyClick(blocking.id, blocking.teamName) : undefined}
                  onRemove={
                    onRemoveBlocking
                      ? () =>
                          onRemoveBlocking(
                            currentDeliverableId,
                            blocking.teamIndex,
                            blocking.deliverableIndex,
                            blocking.id,
                          )
                      : undefined
                  }
                />
              ))
            ) : (
              <span className="text-xs text-text-muted italic">none</span>
            )}
          </div>
          {/* Due date */}
          <div className="flex items-center gap-2">
            <span className="w-24 shrink-0 text-xs text-text-muted">Due date</span>
            <div className="w-32">
              <Controller
                name={`teams.${teamIndex}.deliverables.${deliverableIndex}.due_date`}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    value={field.value || ''}
                    className="w-full"
                    placeholder="Due date"
                    clearable
                    showToday={false}
                  />
                )}
              />
            </div>
          </div>
          {/* Available from */}
          <div className="flex items-center gap-2">
            <span className="w-24 shrink-0 text-xs text-text-muted">Available from</span>
            <div className="w-32">
              <Controller
                name={`teams.${teamIndex}.deliverables.${deliverableIndex}.available_to_start_min`}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    value={field.value || ''}
                    className="w-full"
                    placeholder="From"
                    clearable
                    showToday={false}
                  />
                )}
              />
            </div>
            <span className="text-xs text-text-muted">to</span>
            <div className="w-32">
              <Controller
                name={`teams.${teamIndex}.deliverables.${deliverableIndex}.available_to_start_max`}
                control={control}
                render={({ field }) => (
                  <DatePicker
                    {...field}
                    value={field.value || ''}
                    className="w-full"
                    placeholder="To"
                    clearable
                    showToday={false}
                  />
                )}
              />
            </div>
          </div>
        </div>
      </Collapsible>
    </div>
  );
}

// ============================================================================
// Create Mode Component
// ============================================================================

function DeliverableFormCreate({
  teamId,
  availableFeatures,
  simulationId,
  variant = 'table',
}: DeliverableFormCreateProps) {
  const [name, setName] = useState('');
  const [workItems, setWorkItems] = useState<string>('');
  const [featureId, setFeatureId] = useState<string | null>(null);
  const [unknownMin, setUnknownMin] = useState<string>('');
  const [unknownMax, setUnknownMax] = useState<string>('');
  const [dueDate, setDueDate] = useState('');
  const [errors, setErrors] = useState<{ name?: string; workItems?: string }>({});
  const inputRef = useRef<HTMLInputElement>(null);

  const { handleError } = useErrorHandler('Deliverable Creation');
  const createMutation = useCreateDeliverable(simulationId || '');

  // Convert features to SearchableSelect options
  const savedFeatures = availableFeatures.filter((f) => f.id);
  const featureOptions: SearchableSelectOption[] = useMemo(
    () =>
      savedFeatures.map((f, index) => ({
        value: f.id!,
        label: `${index + 1}. ${f.name}`,
      })),
    [savedFeatures],
  );

  // Summary for collapsed details section
  const createDetailsSummary = useMemo(() => {
    const pills: ReactNode[] = [];
    const base = Number(workItems) || 0;
    const minExtra = Number(unknownMin) || 0;
    const maxExtra = Number(unknownMax) || minExtra;
    const totalMin = base + minExtra;
    const totalMax = base + maxExtra;
    if (totalMin > 0 || totalMax > 0) {
      pills.push(
        <span key="wi" className="rounded-sm bg-bg-muted/50 px-1.5 py-0.5 text-xs text-text-secondary">
          {totalMin === totalMax ? `${totalMin} items` : `${totalMin}–${totalMax} items`}
        </span>,
      );
    }
    if (dueDate) {
      pills.push(
        <span key="due" className="rounded-sm bg-bg-muted/50 px-1.5 py-0.5 text-xs text-text-secondary">
          Due {dueDate}
        </span>,
      );
    }
    if (pills.length === 0) return undefined;
    return <span className="flex flex-wrap items-center gap-1">{pills}</span>;
  }, [workItems, unknownMin, unknownMax, dueDate]);

  const clearForm = () => {
    setName('');
    setWorkItems('');
    setFeatureId(null);
    setUnknownMin('');
    setUnknownMax('');
    setDueDate('');
    setErrors({});
  };

  const handleSubmit = async () => {
    const trimmedName = name.trim();
    const workItemsNum = Number(workItems);
    const newErrors: { name?: string; workItems?: string } = {};

    if (!trimmedName) {
      newErrors.name = 'Name is required';
    }
    if (workItems === '' || isNaN(workItemsNum) || workItemsNum < 0) {
      newErrors.workItems = 'Work items must be a non-negative number';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Parse optional fields
    const minVal = unknownMin === '' ? null : Number(unknownMin);
    const maxVal = unknownMax === '' ? null : Number(unknownMax);

    createMutation.mutate(
      {
        teamId,
        data: {
          feature_id: featureId,
          name: trimmedName,
          unfinished_work_items: workItemsNum,
          unknown_min: minVal,
          unknown_max: maxVal,
          due_date: dueDate || null,
        },
      },
      {
        onSuccess: () => {
          clearForm();
          inputRef.current?.focus();
        },
        onError: (err) => {
          handleError(err);
        },
      },
    );
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    } else if (e.key === 'Escape') {
      clearForm();
    }
  };

  const isSubmitting = createMutation.isPending;
  const canSubmit = name.trim().length > 0 && workItems !== '' && !isSubmitting;

  if (variant === 'table') {
    return (
      <div className="group flex gap-0 transition-all duration-200" data-testid="deliverable-form-create">
        {/* Gutter - "+" indicator (no drag for create mode) */}
        <div
          className="
            flex w-14 shrink-0 items-center justify-center gap-1 rounded-l-xl border-y border-l border-border-default/20
            bg-bg-muted/10 transition-colors
            group-hover:border-border-default/30
            dark:bg-bg-muted/5
          "
        >
          {/* Invisible spacer matching SortableHandle width for alignment */}
          <div className="w-4" />
          <div
            className="
              flex size-7 items-center justify-center rounded-md border border-border-default/40 bg-bg-muted/40 text-sm
              font-normal text-text-muted shadow-xs
              dark:bg-bg-muted/30
            "
          >
            +
          </div>
        </div>

        {/* Card - Content */}
        <div
          className="
            flex flex-1 flex-col overflow-hidden border-y border-border-default/20 bg-bg-elevated transition-colors
            group-hover:border-border-default/30
          "
        >
          {/* Row 1: Primary - Deliverable name + Feature dropdown */}
          <div className="flex items-center gap-2 px-3 py-2.5">
            <div className="min-w-37.5 flex-1">
              <InlineInput
                ref={inputRef}
                type="text"
                size="md"
                placeholder="New deliverable name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
                }}
                onKeyDown={handleKeyDown}
                disabled={isSubmitting}
                error={!!errors.name}
                title={errors.name || undefined}
              />
            </div>

            <div className="w-60 shrink-0">
              <SearchableSelect
                value={featureId}
                onChange={setFeatureId}
                options={featureOptions}
                placeholder="No feature"
                allowClear
                clearLabel="No feature (ungrouped)"
                searchPlaceholder="Search features..."
                noOptionsMessage="No features available"
                disabled={isSubmitting}
              />
            </div>
          </div>

          {/* Details (collapsible) - Work items + Due date */}
          <Collapsible
            header={<span className="text-xs font-medium text-text-secondary">Details</span>}
            summary={createDetailsSummary}
            defaultExpanded={false}
            className="px-3"
            contentClassName="pt-1 pb-2"
          >
            <div className="flex flex-col gap-2">
              {/* Work items with uncertainty range */}
              <div className="flex items-center gap-2">
                <span className="w-24 shrink-0 text-xs text-text-muted">Work items</span>
                <div className="flex items-center gap-1">
                  <div className="w-12">
                    <InlineInput
                      type="number"
                      size="xs"
                      align="center"
                      min={0}
                      placeholder="-"
                      value={workItems}
                      onChange={(e) => {
                        setWorkItems(e.target.value);
                        if (errors.workItems) setErrors((prev) => ({ ...prev, workItems: undefined }));
                      }}
                      onKeyDown={handleKeyDown}
                      disabled={isSubmitting}
                      error={!!errors.workItems}
                      title={errors.workItems || undefined}
                    />
                  </div>
                  <span className="text-xs text-text-muted">plus</span>
                  <div className="w-10">
                    <InlineInput
                      type="number"
                      size="xs"
                      align="center"
                      min={0}
                      placeholder="0"
                      value={unknownMin}
                      onChange={(e) => setUnknownMin(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={isSubmitting}
                      title="Additional unknown work (minimum)"
                    />
                  </div>
                  <span className="text-xs text-text-muted">to</span>
                  <div className="w-10">
                    <InlineInput
                      type="number"
                      size="xs"
                      align="center"
                      min={0}
                      placeholder={unknownMin || '0'}
                      value={unknownMax}
                      onChange={(e) => setUnknownMax(e.target.value)}
                      onKeyDown={handleKeyDown}
                      disabled={isSubmitting}
                      title="Additional unknown work (maximum)"
                    />
                  </div>
                  <span className="text-xs text-text-muted">more</span>
                </div>
              </div>
              {/* Due date */}
              <div className="flex items-center gap-2">
                <span className="w-24 shrink-0 text-xs text-text-muted">Due date</span>
                <div className="w-32">
                  <DatePicker
                    value={dueDate}
                    onChange={setDueDate}
                    className="w-full"
                    placeholder="Due date"
                    clearable
                    showToday={false}
                    disabled={isSubmitting}
                  />
                </div>
              </div>
            </div>
          </Collapsible>
        </div>

        {/* Action column - Create button */}
        <div
          className="
            flex w-12 shrink-0 items-center justify-center rounded-r-xl border-y border-r border-border-default/20
            bg-bg-elevated transition-colors
            group-hover:border-border-default/30
          "
        >
          <IconButton
            type="button"
            variant="primary"
            size="md"
            onClick={handleSubmit}
            disabled={!canSubmit}
            aria-label="Create deliverable"
          >
            {isSubmitting ? <Loader2 className="size-5 animate-spin" /> : <Plus className="size-5" />}
          </IconButton>
        </div>
      </div>
    );
  }

  // Card variant for small screens
  return (
    <div
      className="
        group w-full overflow-hidden rounded-xl border border-border-default/20 bg-bg-elevated transition-all
        duration-200
        hover:border-border-default/30
      "
      data-testid="deliverable-form-create"
    >
      {/* Row 1: Primary - "+" badge + Deliverable name + Create button */}
      <div className="flex items-center gap-2 px-3 py-2.5">
        <div
          className="
            flex size-7 shrink-0 items-center justify-center rounded-md border border-border-default/40 bg-bg-muted/40
            text-sm font-normal text-text-muted shadow-xs
            dark:bg-bg-muted/30
          "
        >
          +
        </div>

        <InlineInput
          ref={inputRef}
          type="text"
          size="md"
          placeholder="New deliverable name"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            if (errors.name) setErrors((prev) => ({ ...prev, name: undefined }));
          }}
          onKeyDown={handleKeyDown}
          disabled={isSubmitting}
          error={!!errors.name}
          title={errors.name || undefined}
          className="min-w-0 flex-1"
        />

        <IconButton
          type="button"
          variant="primary"
          size="sm"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="shrink-0"
          aria-label="Create deliverable"
        >
          {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : <Plus className="size-4" />}
        </IconButton>
      </div>

      {/* Row 2: Feature Selection */}
      <div className="flex items-center gap-2 border-t border-border-default/20 px-3 py-1.5">
        <div className="min-w-0 flex-1">
          <SearchableSelect
            value={featureId}
            onChange={setFeatureId}
            options={featureOptions}
            placeholder="No feature"
            allowClear
            clearLabel="No feature (ungrouped)"
            searchPlaceholder="Search features..."
            noOptionsMessage="No features available"
            disabled={isSubmitting}
          />
        </div>
      </div>

      {/* Details (collapsible) - Work items + Due date */}
      <Collapsible
        header={<span className="text-xs font-medium text-text-secondary">Details</span>}
        summary={createDetailsSummary}
        defaultExpanded={false}
        className="px-3"
        contentClassName="pt-1 pb-2"
      >
        <div className="flex flex-col gap-2">
          {/* Work items with uncertainty range */}
          <div className="flex items-center gap-2">
            <span className="w-24 shrink-0 text-xs text-text-muted">Work items</span>
            <div className="flex items-center gap-1">
              <div className="w-12">
                <InlineInput
                  type="number"
                  size="xs"
                  align="center"
                  min={0}
                  placeholder="-"
                  value={workItems}
                  onChange={(e) => {
                    setWorkItems(e.target.value);
                    if (errors.workItems) setErrors((prev) => ({ ...prev, workItems: undefined }));
                  }}
                  onKeyDown={handleKeyDown}
                  disabled={isSubmitting}
                  error={!!errors.workItems}
                  title={errors.workItems || undefined}
                />
              </div>
              <span className="text-xs text-text-muted">plus</span>
              <div className="w-10">
                <InlineInput
                  type="number"
                  size="xs"
                  align="center"
                  min={0}
                  placeholder="0"
                  value={unknownMin}
                  onChange={(e) => setUnknownMin(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isSubmitting}
                  title="Additional unknown work (minimum)"
                />
              </div>
              <span className="text-xs text-text-muted">to</span>
              <div className="w-10">
                <InlineInput
                  type="number"
                  size="xs"
                  align="center"
                  min={0}
                  placeholder={unknownMin || '0'}
                  value={unknownMax}
                  onChange={(e) => setUnknownMax(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={isSubmitting}
                  title="Additional unknown work (maximum)"
                />
              </div>
              <span className="text-xs text-text-muted">more</span>
            </div>
          </div>
          {/* Due date */}
          <div className="flex items-center gap-2">
            <span className="w-24 shrink-0 text-xs text-text-muted">Due date</span>
            <div className="w-32">
              <DatePicker
                value={dueDate}
                onChange={setDueDate}
                className="w-full"
                placeholder="Due date"
                clearable
                showToday={false}
                disabled={isSubmitting}
              />
            </div>
          </div>
        </div>
      </Collapsible>
    </div>
  );
}
